/**
 * Alerts ▸ System Alerts for the site picked in the title bar — the same page the site shows
 * itself (shared SystemAlertsView, laid out like the Alert List): its system alerts (CPU / memory
 * / disk, cameras not streaming) and system errors (services that stopped), read and acknowledged
 * on the site through its tunnel.
 *
 * The cloud's OWN system alerts are a different page (user menu ▸ System Alerts, /cloud-system-alerts).
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import { nodeEscalationsApi } from '../services/api.service';
import { PageTitle, SystemAlertsView, Callout, BTN_ICON, BTN_SECONDARY_SM } from '@trinetra/shared';

export default function SiteSystemAlertsPage() {
  const { selectedNode } = useNode();
  const { hasPermission } = useAuth();
  const [reload, setReload] = useState(0);
  const [counts, setCounts] = useState<{ alerts: number; errors: number } | null>(null);
  const nodeId = selectedNode?.nodeId || null;
  const api = useMemo(() => (nodeId ? nodeEscalationsApi(nodeId) : null), [nodeId]);

  if (!selectedNode || !api) {
    return (
      <>
        <PageTitle title="System Alerts" subtitle="Acknowledge and resolve a site's system alerts and errors" />
        <Callout tone="info">Pick a site in the title bar to see its system alerts.</Callout>
      </>
    );
  }

  const online = selectedNode.status === 'online';
  const open = counts ? counts.alerts + counts.errors : 0;
  return (
    <>
      <PageTitle
        title="System Alerts"
        subtitle={<>{open > 0 && `${open} not resolved · `}Acknowledge and resolve system alerts and errors for <span className="font-medium text-foreground">{selectedNode.name}</span></>}
      >
        {hasPermission('alerts.config') && (
          <Link to={`/alert-actions?tab=actions&site=${encodeURIComponent(selectedNode.nodeId)}`}
            className={BTN_SECONDARY_SM}>
            Who gets notified
          </Link>
        )}
        <button type="button" onClick={() => setReload((n) => n + 1)} title="Refresh" aria-label="Refresh"
          className={BTN_ICON}>
          <ArrowPathIcon className="h-4 w-4" />
        </button>
      </PageTitle>
      <SystemAlertsView
        key={selectedNode.nodeId}
        api={api}
        canEdit={hasPermission('alerts.edit')}
        reloadToken={reload}
        onCounts={setCounts}
        unavailable={online ? undefined : (
          <>
            <b>{selectedNode.name}</b> is offline, so its system alerts cannot be read right now — they are kept on the site and show here once it reconnects.
            {' '}That it went offline is on <Link to="/system-alerts" className="font-medium text-brand hover:underline">every site&apos;s System Alerts</Link>.
          </>
        )}
      />
    </>
  );
}
